import React, { JSX, useEffect, useRef, useState } from "react";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import { Button } from "components/common/Button";
import { DropdownMenu } from "components/common/DropdownMenu";
import { Callout } from "components/common/Callout";
import { LoadingState } from "components/common/LoadingState";
import { SectionRule } from "components/common/SectionRule";
import { Stack } from "components/common/Stack";
import { StatusChip } from "components/common/StatusChip";
import { Dialog } from "components/main/utils/Dialog";
import { ConfirmDialog } from "components/main/utils/ConfirmDialog";
import { RecipeSummary } from "components/canvas/recipes/RecipeSummary";
import {
  cannotRunText,
  deleteRecipe,
  describeRecipeError,
  fetchRecipe,
  setRecipeDeprecated,
  type RecipeDetail,
  type RecipeListItem,
  type RecipeSlotForm,
} from "components/canvas/recipes/recipesApi";
import { kindHint } from "components/canvas/recipes/runRequest";
import { showNotificationGlobal } from "stores/useNotificationStore";

interface RecipeCatalogDialogProps {
  recipe: RecipeListItem;
  onClose: () => void;
  /** Called after a delete or a deprecation, so the list re-reads. */
  onChanged: () => void;
}

/**
 * A recipe read from the catalog page, where there is no model to run into.
 *
 * It shares its left half with the canvas overlay ([`RecipeSummary`]) and
 * differs in the right: the canvas offers controls because it is about to run,
 * this one describes the shape a run would ask for and offers the author's two
 * acts instead (R26). The dry run still happens — `GET /api/recipes/{id}` plans
 * at the defaults — so a broken recipe says so here exactly as it does there.
 */
export function RecipeCatalogDialog({
  recipe,
  onClose,
  onChanged,
}: RecipeCatalogDialogProps): JSX.Element {
  const [detail, setDetail] = useState<RecipeDetail | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);

  useEffect(() => {
    let cancelled = false;
    fetchRecipe(recipe.recipe_id)
      .then((loaded) => !cancelled && setDetail(loaded))
      .catch((err) => !cancelled && setLoadError(describeRecipeError(err)));
    return () => {
      cancelled = true;
    };
  }, [recipe.recipe_id]);

  const manage = detail?.manage;
  const state = detail?.state ?? recipe.state;

  const toggleDeprecated = async () => {
    if (!detail || !manage || busy) return;
    setBusy(true);
    try {
      await setRecipeDeprecated(detail.recipe_id, !manage.deprecated);
      setDetail({
        ...detail,
        manage: { ...manage, deprecated: !manage.deprecated },
      });
      onChanged();
      showNotificationGlobal(
        manage.deprecated
          ? `"${detail.name}" is back on the shelf`
          : `"${detail.name}" is deprecated`,
      );
    } catch (err) {
      showNotificationGlobal(describeRecipeError(err), "error");
    } finally {
      setBusy(false);
    }
  };

  const runDelete = async () => {
    if (!detail || busy) return;
    setBusy(true);
    setConfirmDelete(false);
    try {
      await deleteRecipe(detail.recipe_id);
      onChanged();
      showNotificationGlobal(`Deleted "${detail.name}"`);
      onClose();
    } catch (err) {
      showNotificationGlobal(describeRecipeError(err), "error");
    } finally {
      setBusy(false);
    }
  };

  const body = (() => {
    if (loadError) {
      return (
        <div className="px-5 py-4">
          <Callout variant="error" title="Could not open this recipe">
            {loadError}
          </Callout>
        </div>
      );
    }
    if (!detail) {
      return (
        <div className="flex h-40 items-center justify-center">
          <LoadingState size="small" isLoading label="Loading recipe…" />
        </div>
      );
    }
    const slots = detail.slots.filter(
      (slot) => slot.kind !== "optional_filter",
    );
    const filters = detail.slots.filter(
      (slot) => slot.kind === "optional_filter",
    );
    return (
      <div className="grid grid-cols-2 gap-6 px-5 py-4">
        <RecipeSummary detail={detail} />

        <Stack gap="lg" className="min-w-0">
          {detail.cannot_run && (
            <Callout
              variant="warning"
              title={
                detail.cannot_run.reason === "categorical_param"
                  ? "This recipe cannot run from the app yet"
                  : "This recipe cannot run"
              }
            >
              {cannotRunText(detail.cannot_run)}
            </Callout>
          )}
          {manage?.deprecated && (
            <Callout variant="warning" title="Deprecated">
              Results built on it still re-run. Don&apos;t build new ones.
            </Callout>
          )}

          {detail.params.length + slots.length > 0 && (
            <Stack gap="xs">
              <SectionRule
                label="Inputs"
                count={detail.params.length + slots.length}
              />
              <dl className="grid grid-cols-[fit-content(9rem)_minmax(0,1fr)] items-center gap-x-4 gap-y-1">
                {detail.params.map((param) => (
                  <React.Fragment key={param.name}>
                    <dt className="truncate text-fg-muted">{param.name}</dt>
                    <dd className="flex min-w-0 items-center gap-2">
                      <span className="truncate text-small text-fg-muted">
                        {kindHint(param)}
                      </span>
                      {filters.some((f) => f.name === param.name) && (
                        <StatusChip>filter</StatusChip>
                      )}
                    </dd>
                  </React.Fragment>
                ))}
                {slots.map((slot) => (
                  <React.Fragment key={slot.name}>
                    <dt className="truncate text-fg-muted">{slot.name}</dt>
                    <dd className="flex min-w-0 items-center gap-2">
                      <SlotPeek slot={slot} />
                      <span className="shrink-0 text-small text-fg-muted">
                        {slot.kind}
                      </span>
                    </dd>
                  </React.Fragment>
                ))}
              </dl>
            </Stack>
          )}

          {manage && (
            <Stack gap="xs">
              <SectionRule label="Manage" />
              {manage.verb === "deprecate" ? (
                <div className="flex items-center gap-3">
                  <Button
                    variant="secondary"
                    size="sm"
                    onClick={toggleDeprecated}
                    loading={busy}
                  >
                    {manage.deprecated ? "Undeprecate" : "Deprecate"}
                  </Button>
                  <span className="text-small text-fg-muted">
                    Published recipes are deprecated, not deleted.
                  </span>
                </div>
              ) : (
                <div className="flex items-center gap-3">
                  <Button
                    variant="destructive"
                    size="sm"
                    onClick={() => setConfirmDelete(true)}
                    disabled={busy || manage.blocked_models > 0}
                    loading={busy}
                  >
                    Delete recipe
                  </Button>
                  <span className="text-small text-fg-muted">
                    {manage.blocked_models > 0
                      ? `${manage.results} result${manage.results === 1 ? "" : "s"} sit in ${manage.blocked_models} model${manage.blocked_models === 1 ? "" : "s"} you cannot delete from.`
                      : manage.results === 0
                        ? "Nothing was built from it."
                        : `${manage.results} result${manage.results === 1 ? "" : "s"} go with it.`}
                  </span>
                </div>
              )}
            </Stack>
          )}
        </Stack>
      </div>
    );
  })();

  return (
    <>
      <Dialog
        open
        title={
          <span className="flex min-w-0 items-center gap-2">
            <span className="truncate text-fg-bold">{recipe.name}</span>
            {/* The loaded recipe outranks the row that opened it: the list is
                a snapshot, so publishing elsewhere would otherwise leave the
                title saying `private` beside a Manage block offering
                Deprecate. */}
            <StatusChip tone={state === "published" ? "accent" : "muted"}>
              {state}
            </StatusChip>
            {(manage?.deprecated ?? recipe.deprecated) && (
              <StatusChip tone="warning">deprecated</StatusChip>
            )}
          </span>
        }
        ariaLabel={`Recipe ${recipe.name}`}
        onClose={onClose}
        size="wide"
        autoSpacing={false}
        backdropStyle={{ paddingLeft: "var(--main-sidebar-width, 0px)" }}
        actions={
          <Button variant="secondary" onClick={onClose}>
            Close
          </Button>
        }
      >
        {body}
      </Dialog>

      <ConfirmDialog
        open={confirmDelete}
        title="Delete this recipe?"
        confirmText="Delete"
        confirmVariant="destructive"
        message={
          manage && manage.results > 0
            ? `"${detail?.name}" and the ${manage.results} result${manage.results === 1 ? "" : "s"} it made go for good. Nothing can rebuild them.`
            : `"${detail?.name}" goes for good.`
        }
        onConfirm={runDelete}
        onCancel={() => setConfirmDelete(false)}
        isLoading={busy}
      />
    </>
  );
}

interface SlotPeekProps {
  slot: RecipeSlotForm;
}

/**
 * What a slot accepts, openable and inert.
 *
 * A reader on this page cannot run anything, so a control that CHANGED the pick
 * would be a lie — but "3 dimensions" hides the one thing worth knowing, which
 * is what the three are. So the trigger names the default a run takes and the
 * panel lists the alternatives; nothing inside it is clickable, which is why
 * this is a plain list in a `DropdownMenu` rather than menu items.
 */
function SlotPeek({ slot }: SlotPeekProps): JSX.Element {
  const [open, setOpen] = useState(false);
  const anchor = useRef<HTMLButtonElement>(null);

  // The stored default is a pick object (`{kind, grain}` / `{kind, column}`),
  // and `allowed` carries the reader's label for each — both from the same
  // serializer, so matching them as JSON is exact.
  const key = (value: unknown) => JSON.stringify(value);
  const current =
    slot.allowed.find((choice) => key(choice.value) === key(slot.default))
      ?.label ?? "—";

  // An optional filter is present or absent, not one of a set: there is
  // nothing to list.
  if (slot.allowed.length === 0) {
    return <span className="truncate text-small text-fg-muted">{current}</span>;
  }

  return (
    <>
      <Button
        ref={anchor}
        variant="secondary"
        size="sm"
        onClick={() => setOpen((v) => !v)}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-label={`${slot.name} accepts ${slot.allowed.length} values`}
      >
        {current}
        <KeyboardArrowDownIcon className="icon-xs" />
      </Button>
      <DropdownMenu
        anchorRef={anchor}
        isOpen={open}
        onClose={() => setOpen(false)}
        // `bottom` pins the menu to the trigger's width, which is sized to the
        // default alone — every other value, and the `default` hint beside it,
        // would be squeezed to a sliver. The trigger stays the floor.
        // Capped because `bottom` placement pins `left` and never clamps to the
        // viewport: a long column name would otherwise run off the right edge.
        style={{
          width: "max-content",
          minWidth: anchor.current?.offsetWidth,
          maxWidth: "20rem",
        }}
      >
        <ul role="listbox" aria-label={slot.name} className="list-none py-1">
          {slot.allowed.map((choice) => {
            const isDefault = key(choice.value) === key(slot.default);
            return (
              <li
                key={choice.label}
                role="option"
                aria-selected={isDefault}
                className={`flex items-center justify-between gap-4 px-3 py-1 ${
                  isDefault ? "text-fg" : "text-fg-muted"
                }`}
              >
                <span className="truncate">{choice.label}</span>
                {isDefault && (
                  <span className="shrink-0 text-small text-fg-muted">
                    default
                  </span>
                )}
              </li>
            );
          })}
        </ul>
      </DropdownMenu>
    </>
  );
}
